/**
 * Score-Result-Schema — was die Score-Engine liefert und was persistiert wird.
 *
 * Ein Score ist immer dimensionsweise UND als gewichteter Total-Score
 * abrufbar. Konfidenz spiegelt die Datenvollständigkeit wider — unter 60%
 * gilt der Score nach Konzept §5.5 nicht als abschließend.
 */
import { z } from 'zod';
import { PercentageSchema, ScoreValueSchema } from './brands.js';
import { DimensionKeySchema } from './dimensionData.js';
import { CaseIdSchema, ScoreIdSchema } from './ids.js';

export const TrafficLightSchema = z.enum(['green', 'yellow', 'red']);
export type TrafficLight = z.infer<typeof TrafficLightSchema>;

export const RiskIndicatorSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type RiskIndicatorSeverity = z.infer<typeof RiskIndicatorSeveritySchema>;

/**
 * Stabiler maschinenlesbarer Schlüssel pro Risikoindikator. Wird in der
 * Stellungnahme zu einem Textbaustein gemappt — daher additiv erweitern,
 * niemals umbenennen.
 */
export const RiskIndicatorCodeSchema = z.enum([
  // Identity
  'identity_industry_outsider',
  'identity_no_sales_experience',
  // Activity
  'activity_klumpenrisiko',
  'activity_no_recurring_revenue',
  'activity_no_sales_process',
  // Style
  'style_undefined_target_group',
  'style_hourly_rate_below_floor',
  'style_no_pricing_strategy',
  // Resources
  'resources_negative_liquidity',
  'resources_profit_below_threshold',
  'resources_low_billable_hours',
  'resources_negative_hourly_rate',
  // Health
  'health_chronic_condition_affects_work',
  'health_overwork',
  'health_low_resilience',
  // Private / BG
  'private_bg_gap_large',
  'private_no_childcare',
  'private_high_care_burden',
]);
export type RiskIndicatorCode = z.infer<typeof RiskIndicatorCodeSchema>;

export const RiskIndicatorSchema = z.object({
  dimension: DimensionKeySchema,
  code: RiskIndicatorCodeSchema,
  severity: RiskIndicatorSeveritySchema,
  message: z.string().min(1).max(500),
});
export type RiskIndicator = z.infer<typeof RiskIndicatorSchema>;

export const DimensionScoreSchema = z.object({
  dimension: DimensionKeySchema,
  score: ScoreValueSchema,
  /** Datenvollständigkeit der Pflichtfelder dieser Dimension, 0-100 */
  confidencePercent: PercentageSchema,
});
export type DimensionScore = z.infer<typeof DimensionScoreSchema>;

/**
 * Score-Vollständigkeitsstatus:
 *   - `unscored`     — keine Dimension hat Daten, Score ist nur ein Platzhalter
 *   - `preliminary`  — mindestens eine, aber nicht alle Dimensionen erfasst
 *                      ODER Konfidenz unter Schwelle (Konzept §5.5: 60%)
 *   - `final`        — alle 6 Dimensionen erfasst UND Konfidenz ≥ Schwelle
 *
 * Ein `preliminary` Score darf NIE in einer formellen Stellungnahme als
 * abschließend ausgewiesen werden — die Berater-UI muss das deutlich
 * kennzeichnen. Ein `unscored` Score ist nur ein UI-Anzeige-Hilfsmittel.
 */
export const ScoreStateSchema = z.enum(['unscored', 'preliminary', 'final']);
export type ScoreState = z.infer<typeof ScoreStateSchema>;

export const ScoreResultSchema = z.object({
  /**
   * Pro erfasster Dimension ein Eintrag. Untouched/leere Dimensionen
   * erscheinen NICHT — die UI muss anhand `dimensionsScored` entscheiden,
   * was bereits gewertet wurde.
   */
  dimensionScores: z.array(DimensionScoreSchema).max(6),
  /** Welche Dimensionen tatsächlich in den Score eingeflossen sind. */
  dimensionsScored: z.array(DimensionKeySchema),
  totalScore: ScoreValueSchema,
  totalConfidencePercent: PercentageSchema,
  trafficLight: TrafficLightSchema,
  /** Vollständigkeitsstatus, siehe `ScoreStateSchema`. */
  state: ScoreStateSchema,
  riskIndicators: z.array(RiskIndicatorSchema),
  /** Zeitstempel des Berechnungslaufs */
  computedAt: z.coerce.date(),
});
export type ScoreResult = z.infer<typeof ScoreResultSchema>;

/** Persistierter Score-Snapshot (History pro Case). */
export const PersistedScoreSchema = ScoreResultSchema.extend({
  id: ScoreIdSchema,
  caseId: CaseIdSchema,
});
export type PersistedScore = z.infer<typeof PersistedScoreSchema>;
